// Billing data + mutations (PRD §4.10, §8) — live. Reads the tenant's real
// plan/subscription, its invoices, and the current month's message usage; writes
// go through the Amwal billing callables. Plan pricing/quotas mirror
// functions/src/tenancy/plans.ts (kept in sync manually — small + rarely changes).
import { useCallback, useEffect, useState } from 'react';
import {
  collection, doc, getCountFromServer, onSnapshot, orderBy, query,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions, geoResolveUrl } from './firebase';
import { useTenant } from './tenant';

export type BillingCycle = 'monthly' | 'yearly';
export type InvoiceStatus = 'paid' | 'pending' | 'failed';

export interface PlanQuotas {
  subscribers: number;
  messagesPerMonth: number;
  aiTokensPerMonth: number;
  teamSeats: number;
  numbers: number;
}
export interface PlanDef {
  id: string;
  name: string;
  monthlyUsd: number;
  yearlyUsd: number;
  isTrial: boolean;
  /** Days free on the first subscribe. >0 drives the "first month free" badge. */
  trialDays?: number;
  /** Free forever — never charged, never lapses. */
  isFree?: boolean;
  /** Price per currency; the customer sees their own, Amwal charges the rial. */
  prices?: Record<string, PriceRow>;
  quotas: PlanQuotas;
}

export const PLAN_CATALOG: Record<string, PlanDef> = {
  trial: {
    id: 'trial', name: 'Free', monthlyUsd: 0, yearlyUsd: 0, isTrial: true,
    trialDays: 0, isFree: true,
    quotas: { subscribers: 100, messagesPerMonth: 500, aiTokensPerMonth: 10_000, teamSeats: 1, numbers: 1 },
  },
  pro: {
    id: 'pro', name: 'Pro', monthlyUsd: 1.15, yearlyUsd: 11.5, isTrial: false, trialDays: 30,
    quotas: { subscribers: 5_000, messagesPerMonth: 10_000, aiTokensPerMonth: 100_000, teamSeats: 3, numbers: 1 },
  },
  business: {
    id: 'business', name: 'Business', monthlyUsd: 3.841, yearlyUsd: 38.41, isTrial: false, trialDays: 30,
    quotas: { subscribers: 25_000, messagesPerMonth: 50_000, aiTokensPerMonth: 500_000, teamSeats: 10, numbers: 3 },
  },
};

export interface Invoice {
  id: string;
  planId: string;
  cycle: BillingCycle;
  amountUsd: number;
  currency: string;
  status: InvoiceStatus;
  createdAt: number;
  paidAt: number | null;
}

export interface BillingState {
  planId: string;
  plan: PlanDef;
  cycle: BillingCycle;
  status: string;
  renewalAt: number | null;
  cancelAtPeriodEnd: boolean;
  invoices: Invoice[];
  usageMessages: number;
  subscribers: number;
  /** Live plan catalog (Firestore `plans/*`, falling back to the static mirror). */
  catalog: Record<string, PlanDef>;
  /** Staff-granted extras (bonus messages / free-until) — shown, and honoured server-side. */
  bonusMessages: number;
  freeUntil: number | null;
  /** True once the workspace has taken its one free month on a paid plan. */
  freeMonthUsed: boolean;
  /** Raw per-tenant quota overrides from the tenant doc (applied into `plan`). */
  overrides: Partial<PlanQuotas> | null;
  /** VAT the tenant will be charged, resolved from their billing country. */
  taxRatePct: number;
  taxLabel: string;
  loading: boolean;
  error?: string | null;
}

/** Mirror of functions/src/billing/tax.ts rateFor — same resolution order. */
function resolveTaxRate(taxCfg: any, country: string): number {
  if (!taxCfg?.enabled) return 0;
  const cc = (country ?? '').trim().toUpperCase();
  const explicit = cc ? taxCfg.rates?.[cc] : undefined;
  const rate = typeof explicit === 'number' ? explicit : Number(taxCfg.defaultRatePct ?? 0);
  return Number.isFinite(rate) && rate >= 0 ? rate : 0;
}

/** Price with VAT applied, rounded to cents (mirrors applyTax). */
export function withTax(subtotalUsd: number, ratePct: number): number {
  const tax = Math.round(((subtotalUsd * ratePct) / 100) * 100) / 100;
  return Math.round((subtotalUsd + tax) * 100) / 100;
}

/**
 * Merge a Firestore plan doc over the static mirror. `plans/{id}` is what
 * staff edit at runtime and what checkout actually charges; showing the static
 * price here would let a tenant see $14 and be invoiced $20.
 */
export function planFromDoc(id: string, d: any, fallback?: PlanDef): PlanDef | null {
  if (!d && !fallback) return null;
  const q = d?.quotas ?? {};
  const fq = fallback?.quotas;
  return {
    id,
    name: d?.name ?? fallback?.name ?? id,
    monthlyUsd: Number(d?.priceMonthlyUsd ?? fallback?.monthlyUsd ?? 0),
    yearlyUsd: Number(d?.priceYearlyUsd ?? fallback?.yearlyUsd ?? 0),
    isTrial: Boolean(d?.isTrial ?? fallback?.isTrial ?? false),
    trialDays: Number(d?.trialDays ?? fallback?.trialDays ?? 0),
    isFree: Boolean(d?.isFree ?? fallback?.isFree ?? false),
    prices: (d?.prices && typeof d.prices === 'object') ? d.prices : fallback?.prices,
    quotas: {
      subscribers: Number(q.subscribers ?? fq?.subscribers ?? 0),
      messagesPerMonth: Number(q.messagesPerMonth ?? fq?.messagesPerMonth ?? 0),
      aiTokensPerMonth: Number(q.aiTokensPerMonth ?? fq?.aiTokensPerMonth ?? 0),
      teamSeats: Number(q.teamSeats ?? fq?.teamSeats ?? 0),
      numbers: Number(q.numbers ?? fq?.numbers ?? 1),
    },
  };
}

/** Per-tenant overrides win over the plan's quotas (mirrors quota.tenantQuotas). */
export function applyOverrides(plan: PlanDef, overrides: any): PlanDef {
  if (!overrides || typeof overrides !== 'object') return plan;
  const q = { ...plan.quotas };
  for (const k of Object.keys(q) as (keyof PlanQuotas)[]) {
    const v = overrides[k];
    if (typeof v === 'number' && Number.isFinite(v) && v >= 0) q[k] = v;
  }
  return { ...plan, quotas: q };
}

/**
 * WaTag bills in Omani Rial via Amwal Pay. The stored fields are named
 * `...Usd` for historical reasons (see functions/src/tenancy/plans.ts) but hold
 * OMR, so every amount must render through this helper — never a bare `$`.
 * OMR is quoted to 3 decimals (1 rial = 1000 baisa).
 */
export const BILLING_CURRENCY = 'OMR';

export interface CurrencyDef { code: string; rateToOmr: number; symbol: string; decimals: number }
export interface PriceRow { monthly: number; yearly: number }

/**
 * Enough of the currency table to render before `geoResolve` answers. The
 * server's copy (functions/src/billing/pricing.ts) is authoritative and
 * replaces this as soon as the fetch lands.
 */
export const FALLBACK_CURRENCIES: Record<string, CurrencyDef> = {
  OMR: { code: 'OMR', rateToOmr: 1, symbol: 'ر.ع.', decimals: 3 },
  USD: { code: 'USD', rateToOmr: 0.384497, symbol: '$', decimals: 2 },
};

/** The rial amount actually charged, for receipts and the operator console. */
export function moneyOmr(amount: number | string | null | undefined): string {
  const n = Number(amount ?? 0);
  if (!Number.isFinite(n)) return `0.000 ${BILLING_CURRENCY}`;
  return `${n.toFixed(3)} ${BILLING_CURRENCY}`;
}

/**
 * Format an amount that is ALREADY in `currency` — this does not convert.
 * Arabic reads the symbol after the number.
 */
export function formatMoney(
  amount: number | null | undefined,
  currency: string,
  currencies: Record<string, CurrencyDef>,
  lang = 'en',
): string {
  const def = currencies[currency] ?? currencies.USD ?? FALLBACK_CURRENCIES.USD;
  const n = Number(amount ?? 0).toFixed(def.decimals);
  const sym = def.symbol || currency;
  if (lang === 'ar') return `${n} ${sym}`;
  // A bare glyph hugs the number; a letter code needs a space.
  return /^[^A-Za-z]+$/.test(sym) ? `${sym}${n}` : `${sym} ${n}`;
}

/** What Amwal will actually take, in rials, for a local price. */
export function omrChargeFor(
  amount: number,
  currency: string,
  currencies: Record<string, CurrencyDef>,
): number {
  const def = currencies[currency] ?? currencies.USD ?? FALLBACK_CURRENCIES.USD;
  return Math.round(amount * def.rateToOmr * 1000) / 1000;
}

/**
 * Convert a rial figure into `currency`. Used only when a plan has no price
 * row for that market yet — without it the card would silently fall back to
 * rials and the currency picker would look broken.
 */
export function fromOmr(
  omr: number,
  currency: string,
  currencies: Record<string, CurrencyDef>,
): number {
  const def = currencies[currency] ?? currencies.USD ?? FALLBACK_CURRENCIES.USD;
  const rate = def.rateToOmr || 1;
  const v = omr / rate;
  // Round to the currency's own precision so we never show 2.9909999.
  const f = 10 ** (def.decimals ?? 2);
  return Math.round(v * f) / f;
}

/** The price a plan shows in `currency`, falling back through USD. */
export function priceIn(
  prices: Record<string, PriceRow> | undefined,
  currency: string,
  cycle: BillingCycle,
): number | null {
  const row = prices?.[currency] ?? prices?.USD;
  const v = Number(row?.[cycle]);
  return Number.isFinite(v) ? v : null;
}

const LS_CURRENCY = 'watag.currency';

/**
 * Country from the browser's IANA timezone.
 *
 * The server reads Google's edge header, but that is only attached when the
 * request arrives through Firebase Hosting — a direct call to the function URL
 * gets nothing, and some networks strip it. The timezone is already on the
 * device, costs no request, and is far more reliable than the browser locale
 * (a phone set to English/US sitting in Muscat still reports Asia/Muscat).
 */
const TZ_COUNTRY: Record<string, string> = {
  'Asia/Muscat': 'OM',
  'Asia/Riyadh': 'SA',
  'Asia/Dubai': 'AE',
  'Asia/Qatar': 'QA',
  'Asia/Kuwait': 'KW',
  'Asia/Bahrain': 'BH',
  'Asia/Amman': 'JO',
  'Asia/Baghdad': 'IQ',
  'Asia/Beirut': 'LB',
  'Asia/Damascus': 'SY',
  'Asia/Gaza': 'PS', 'Asia/Hebron': 'PS',
  'Asia/Aden': 'YE',
  'Africa/Cairo': 'EG',
  'Africa/Casablanca': 'MA',
  'Africa/Tunis': 'TN',
  'Africa/Algiers': 'DZ',
  'Africa/Tripoli': 'LY',
  'Africa/Khartoum': 'SD',
  'Africa/Mogadishu': 'SO',
  'Africa/Djibouti': 'DJ',
  'Africa/Nouakchott': 'MR',
};

export function countryFromTimezone(): string | null {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
    if (TZ_COUNTRY[tz]) return TZ_COUNTRY[tz];
    // Anything else in Europe is quoted in euro anyway, so the region is
    // enough — no need to enumerate every European timezone.
    if (tz.startsWith('Europe/')) return tz === 'Europe/London' ? 'GB' : 'DE';
    return null;
  } catch {
    return null;
  }
}

export interface PricingCtx {
  /** Currency the customer is quoted in. */
  currency: string;
  currencies: Record<string, CurrencyDef>;
  /** Detected ISO country, or null when the edge header was absent. */
  country: string | null;
  /** Replace the detected currency; persists for this browser. */
  setCurrency: (code: string) => void;
  loading: boolean;
}

/**
 * Resolves which currency to quote. Order of precedence:
 *   1. a currency the user picked (localStorage)
 *   2. the country Google's edge reports for their IP
 *   3. USD
 * The picked value wins on purpose — someone browsing from abroad should be
 * able to see the prices they will actually be billed against.
 */
export function usePricing(): PricingCtx {
  const [state, setState] = useState<{ currency: string; currencies: Record<string, CurrencyDef>; country: string | null; loading: boolean }>({
    currency: (() => {
      try { return localStorage.getItem(LS_CURRENCY) || 'USD'; } catch { return 'USD'; }
    })(),
    currencies: FALLBACK_CURRENCIES,
    country: null,
    loading: true,
  });

  useEffect(() => {
    let alive = true;
    // Same-origin '/geo' goes through Firebase Hosting, which is what attaches
    // the country header; the direct function URL is only a fallback for local
    // dev, where it returns no country and we lean on the timezone instead.
    fetch('/geo').then((r) => (r.ok ? r : Promise.reject(new Error('rewrite missing'))))
      .catch(() => fetch(geoResolveUrl))
      .then((r) => r.json())
      .then((d) => {
        if (!alive) return;
        let picked: string | null = null;
        try { picked = localStorage.getItem(LS_CURRENCY); } catch { /* private mode */ }
        const currencies = (d?.currencies && typeof d.currencies === 'object') ? d.currencies : FALLBACK_CURRENCIES;
        const map = (d?.countryCurrency && typeof d.countryCurrency === 'object') ? d.countryCurrency : {};

        // Edge header first; timezone when it is absent; USD last.
        const country = d?.country || countryFromTimezone();
        const detected = (country && map[country]) || d?.currency || 'USD';

        setState({
          currencies,
          country: country ?? null,
          currency: (picked && currencies[picked]) ? picked : detected,
          loading: false,
        });
      })
      // Never block the billing page on a geo lookup — fall through to USD.
      .catch(() => { if (alive) setState((s) => ({ ...s, loading: false })); });
    return () => { alive = false; };
  }, []);

  const setCurrency = useCallback((code: string) => {
    try { localStorage.setItem(LS_CURRENCY, code); } catch { /* private mode */ }
    setState((s) => ({ ...s, currency: code }));
  }, []);

  return { ...state, setCurrency };
}

function monthKey(now = Date.now()): string {
  const d = new Date(now);
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
}

export function useBilling(): BillingState {
  const { tenantId } = useTenant();
  const [state, setState] = useState<BillingState>({
    planId: 'trial', plan: PLAN_CATALOG.trial, cycle: 'monthly', status: 'trial',
    renewalAt: null, cancelAtPeriodEnd: false, invoices: [], usageMessages: 0, subscribers: 0,
    catalog: PLAN_CATALOG, bonusMessages: 0, freeUntil: null, freeMonthUsed: false, overrides: null,
    taxRatePct: 0, taxLabel: 'VAT', loading: true,
  });
  // platform/config is readable by any signed-in user (firestore.rules).
  const [taxCfg, setTaxCfg] = useState<any>(null);
  const [country, setCountry] = useState('');

  useEffect(() => onSnapshot(
    doc(db, 'platform', 'config'),
    (snap) => setTaxCfg(snap.data()?.tax ?? null),
    () => setTaxCfg(null),
  ), []);

  useEffect(() => {
    setState((s) => ({
      ...s,
      taxRatePct: resolveTaxRate(taxCfg, country),
      taxLabel: (taxCfg?.label as string) || 'VAT',
    }));
  }, [taxCfg, country]);

  useEffect(() => {
    if (!tenantId) { setState((s) => ({ ...s, loading: false })); return; }

    // Live catalog: Firestore plans (signed-in readable per rules) over the
    // static mirror. Archived plans are hidden from the picker.
    const unsubPlans = onSnapshot(collection(db, 'plans'), (snap) => {
      const catalog: Record<string, PlanDef> = {};
      for (const [id, p] of Object.entries(PLAN_CATALOG)) catalog[id] = p;
      for (const x of snap.docs) {
        const d = x.data() as any;
        if (d?.archived === true) { delete catalog[x.id]; continue; }
        const merged = planFromDoc(x.id, d, PLAN_CATALOG[x.id]);
        if (merged) catalog[x.id] = merged;
      }
      setState((s) => ({
        ...s,
        catalog,
        plan: applyOverrides(catalog[s.planId] ?? s.plan, s.overrides),
      }));
    }, () => {});

    const unsubTenant = onSnapshot(doc(db, `tenants/${tenantId}`), (snap) => {
      const d = snap.data() as any;
      const sub = d?.subscription ?? {};
      const planId = (d?.planId as string) ?? 'trial';
      const credits = d?.credits ?? {};
      const overrides = (d?.overrides && typeof d.overrides === 'object') ? d.overrides as Partial<PlanQuotas> : null;
      setCountry((d?.billingCountry as string) || (d?.country as string) || '');
      setState((s) => ({
        ...s,
        planId,
        plan: applyOverrides(s.catalog[planId] ?? PLAN_CATALOG[planId] ?? PLAN_CATALOG.trial, overrides),
        overrides,
        cycle: sub.cycle === 'yearly' ? 'yearly' : 'monthly',
        status: (sub.status as string) ?? (d?.status as string) ?? 'trial',
        renewalAt: sub.periodEnd ?? d?.renewalAt ?? null,
        cancelAtPeriodEnd: Boolean(sub.cancelAtPeriodEnd),
        bonusMessages: Number(credits.bonusMessages ?? 0),
        freeUntil: typeof credits.freeUntil === 'number' ? credits.freeUntil : null,
        freeMonthUsed: Boolean(d?.freeMonthUsedAt),
        loading: false,
      }));
    }, () => setState((s) => ({ ...s, loading: false })));

    const unsubInv = onSnapshot(
      query(collection(db, `tenants/${tenantId}/invoices`), orderBy('createdAt', 'desc')),
      (snap) => {
        const invoices = snap.docs.map((x) => {
          const v = x.data() as any;
          return {
            id: x.id, planId: v.planId, cycle: v.cycle, amountUsd: v.amountUsd ?? 0,
            currency: v.currency ?? 'USD',
            status: (v.status === 'paid' ? 'paid' : v.status === 'failed' ? 'failed' : 'pending') as InvoiceStatus,
            createdAt: v.createdAt ?? 0, paidAt: v.paidAt ?? null,
          };
        });
        setState((s) => ({ ...s, invoices }));
      },
      () => {},
    );

    const unsubUsage = onSnapshot(doc(db, `tenants/${tenantId}/usage/${monthKey()}`), (snap) => {
      setState((s) => ({ ...s, usageMessages: Number(snap.data()?.messages ?? 0) }));
    }, () => {});

    getCountFromServer(collection(db, `tenants/${tenantId}/subscribers`))
      .then((c) => setState((s) => ({ ...s, subscribers: c.data().count })))
      .catch((err) => setState((s) => ({ ...s, error: (err as Error).message })));

    return () => { unsubPlans(); unsubTenant(); unsubInv(); unsubUsage(); };
  }, [tenantId]);

  return state;
}

// ── mutations ────────────────────────────────────────────────────────────────
// `activated` is true when a 100%-off coupon settled the invoice server-side —
// there is no gateway step, the plan is already live.
const checkoutFn = httpsCallable<{ planId: string; cycle: BillingCycle; couponCode?: string }, { ok: boolean; invoiceId: string; amountUsd: number; currency: string; checkoutUrl: string | null; activated?: boolean }>(functions, 'billingCreateCheckout');
const cancelFn = httpsCallable<Record<string, never>, { ok: boolean }>(functions, 'billingCancel');
// callbackSecret is returned ONLY on the call that generates it; afterwards the
// server sends null and just confirms one exists.
const initFn = httpsCallable<{ amwalCheckoutUrl?: string }, { ok: boolean; callbackSecret: string | null; hasSecret: boolean; amwalCheckoutUrl: string | null }>(functions, 'billingInitConfig');

export interface CouponCheck {
  ok: boolean;
  reason: string | null;
  code: string;
  label: string | null;
  listAmountUsd: number;
  /** Tax-inclusive total the customer will actually be charged. */
  amountUsd: number;
  discountUsd: number;
  /** Post-discount, pre-tax. */
  subtotalUsd: number;
  taxUsd: number;
  taxRatePct: number;
  taxLabel: string | null;
}

const validateCouponFn = httpsCallable<{ code: string; planId: string; cycle: BillingCycle }, CouponCheck>(functions, 'billingValidateCoupon');

/** Preview a discount code before sending the customer to the payment page. */
export function validateCoupon(code: string, planId: string, cycle: BillingCycle) {
  return validateCouponFn({ code, planId, cycle }).then((r) => r.data);
}

export function createCheckout(planId: string, cycle: BillingCycle, couponCode?: string) {
  return checkoutFn(couponCode ? { planId, cycle, couponCode } : { planId, cycle }).then((r) => r.data);
}
export function cancelSubscription() {
  return cancelFn({}).then((r) => r.data);
}
export function initBillingConfig(amwalCheckoutUrl?: string) {
  return initFn(amwalCheckoutUrl ? { amwalCheckoutUrl } : {}).then((r) => r.data);
}
